import type {SyntheticEvent, ReactElement} from 'react'
import {asHTMLAttributeValue} from '@freckle/maybe'
import {
  standardCheckbox,
  labelStyle,
  labelWrapper,
  smallCheckbox,
  largeCheckbox,
  disabledStyle,
} from './checkbox.module.scss'

type SizeT = 'sm' | 'lg'

type Props = {
  id: string
  onChange: (e: SyntheticEvent<EventTarget, Event>) => void
  size?: SizeT
  autoFocus?: boolean
  checked?: boolean
  disabled?: boolean
  label?: string | ReactElement
  name?: string
  value?: string
  dataTest?: string
  tabIndex?: string
}

function Checkbox(props: Props) {
  const {
    id,
    label,
    autoFocus = false,
    checked = false,
    disabled = false,
    name,
    value,
    dataTest,
    onChange,
    size = 'sm',
    tabIndex = '0',
  } = props

  const nameObj = {name: asHTMLAttributeValue(name)}
  const disabledObj = {disabled: disabled ? true : undefined}
  const autoFocusObj = {autoFocus: autoFocus ? true : undefined}
  const dataTestObj = {'data-test': asHTMLAttributeValue(dataTest)}
  const dataTestInputObj = {
    'data-test': dataTest !== undefined ? `${dataTest}-input` : undefined,
  }

  const tabIndexObj = {tabIndex: parseInt(tabIndex, 10)}

  const additionalProps = {
    ...nameObj,
    ...disabledObj,
    ...autoFocusObj,
    ...dataTestInputObj,
  }

  const checkboxSize = getStylesForSize(size)

  const disabledClassStr = disabled ? disabledStyle : ''

  return (
    <label className={labelWrapper} htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        value={value}
        checked={checked}
        onChange={onChange}
        {...additionalProps}
      />
      <span
        className={`${standardCheckbox} ${checkboxSize} ${disabledClassStr}`}
        {...tabIndexObj}
        {...dataTestObj}
      />
      <span className={labelStyle}>{label}</span>
    </label>
  )
}

function getStylesForSize(size: SizeT): string {
  switch (size) {
    case 'sm':
      return smallCheckbox

    case 'lg':
      return largeCheckbox

    default:
      throw new Error(`No style available for selected checkbox size: ${size}`)
  }
}

export {Checkbox}
